import React, { useEffect, useMemo, type FunctionComponent } from "react";
import {
  CheckOutlined,
  ClockCircleOutlined,
  MoreOutlined,
  NotificationOutlined,
  SortAscendingOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Drawer, Dropdown, Space, Tabs, type MenuProps, type TabsProps } from "antd";
import { useTranslation } from "react-i18next";

import { chatActions } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { ChatStatus } from "@fitness-app/data-models/entities/Chat";

import { usePermission } from "~/hooks/usePermission";
import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import AnnouncementChannelModal from "~/modules/Chat/AnnouncementChannel/AnnouncementChannelModal";
import ChatChannelsList from "~/modules/Chat/ChatChannelsList";
import { usePendingChatSuggestionChannels } from "~/modules/Chat/hooks/usePendingChatSuggestionChannels";
import PendingChatSuggestionsList from "~/modules/Chat/PendingChatSuggestionsList";
import { SendMessageToMultipleUsersModal } from "~/modules/Chat/SendMessageToMultipleUsers/SendMessageToMultipleUsersModal";
import UnreadChatsList from "~/modules/Chat/UnreadChatsList";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

interface OwnProps {
  isOpen: boolean;
  toggleDrawer: () => void;
}

type Props = OwnProps;
type ChatDrawerTabKey = ChatStatus | "unread" | "pendingConfirmation";

export const CHAT_DRAWER_WIDTH = 460;

const ChatDrawer: FunctionComponent<Props> = ({ toggleDrawer, isOpen }) => {
  const { t } = useTranslation(["dashboard", "common", "chat"]);
  const { channelsListStatus, channels, archivedChannels, archivedListStatus, unreadMessages } = useAppSelector(
    (store) => store.chat,
  );
  const { memberId } = useUserClaims();
  const canWriteChat = usePermission(PermissionKey.CHAT_WRITE);
  const canUseAgents = usePermission(PermissionKey.AGENTS_USE);
  const canReviewAgentSuggestions = canWriteChat && canUseAgents;
  // Announcement channels are tenant-wide, so the panel needs the tenant-wide chat scope.
  const canManageAnnouncementChannel = usePermission(PermissionKey.CHAT_READ, PermissionScope.ALL);
  // Backfill (channel creation) needs the tenant-wide WRITE scope — read-only viewers get a disabled action.
  const canWriteAnnouncementChannel = usePermission(PermissionKey.CHAT_WRITE, PermissionScope.ALL);
  const totalUnread = useAppSelector((store) => store.chat.unreadMessages?.total);
  const { data: pendingChatSuggestionChannels } = usePendingChatSuggestionChannels({
    enabled: isOpen && canReviewAgentSuggestions,
  });

  const [activeTab, setActiveTab] = React.useState<ChatDrawerTabKey>("unread");
  const [showMultipleUsersModal, setShowMultipleUsersModal] = React.useState(false);
  const [showAnnouncementChannelModal, setShowAnnouncementChannelModal] = React.useState(false);
  const [sortByResponseTime, setSortByResponseTime] = React.useState(false);
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const pendingConfirmationCount = pendingChatSuggestionChannels?.count || 0;

  useEffect(() => {
    if (isOpen) {
      dispatch(chatActions.subscribeToUnreadMessages());
    }
  }, [isOpen]);

  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: ["channelResponseMetrics"] });
  }, [totalUnread]);

  const items: TabsProps["items"] = [
    {
      key: "unread",
      label: (
        <Space>
          Nieodczytane
          <Badge count={totalUnread} overflowCount={99} />
        </Space>
      ),
      children:
        activeTab === "unread" ? (
          <UnreadChatsList changeTab={setActiveTab} sortByResponseTime={sortByResponseTime} />
        ) : null,
    },
    {
      key: ChatStatus.Active,
      label: (
        <Space>
          {t("chatDrawer.channel.active")}
          <Badge count={0} overflowCount={99} />
        </Space>
      ),
      children:
        activeTab === ChatStatus.Active ? (
          <ChatChannelsList
            changeTab={setActiveTab}
            channels={channels}
            status={channelsListStatus}
            chatStatus={ChatStatus.Active}
            sortByResponseTime={sortByResponseTime}
          />
        ) : null,
    },
    ...(memberId && canReviewAgentSuggestions
      ? [
          {
            key: "pendingConfirmation",
            label: (
              <Space>
                {t("chatDrawer.channel.pendingConfirmation")}
                <Badge count={pendingConfirmationCount} overflowCount={99} />
              </Space>
            ),
            children:
              activeTab === "pendingConfirmation" ? (
                <PendingChatSuggestionsList changeTab={setActiveTab} sortByResponseTime={sortByResponseTime} />
              ) : null,
          },
        ]
      : []),
    {
      key: ChatStatus.Archived,
      label: <span>{t("chatDrawer.channel.archived")}</span>,
      children:
        activeTab === ChatStatus.Archived ? (
          <ChatChannelsList
            changeTab={setActiveTab}
            channels={archivedChannels}
            status={archivedListStatus}
            chatStatus={ChatStatus.Archived}
          />
        ) : null,
    },
  ];

  const dropdown: MenuProps["items"] = useMemo(
    () => [
      {
        key: "sort",
        label: sortByResponseTime ? t("chat:sorting.default") : t("chat:sorting.byResponseTime"),
        icon: sortByResponseTime ? <SortAscendingOutlined /> : <ClockCircleOutlined />,
        onClick: () => setSortByResponseTime((prev) => !prev),
      },
      ...(canWriteChat
        ? [
            {
              type: "divider" as const,
            },
            {
              key: "1",
              label: t("chatDrawer.sendToMultiple"),
              icon: <TeamOutlined />,
              onClick: () => setShowMultipleUsersModal(true),
            },
          ]
        : []),
      ...(canManageAnnouncementChannel
        ? [
            {
              key: "announcementChannel",
              label: "Kanał ogłoszeniowy",
              icon: <NotificationOutlined />,
              onClick: () => setShowAnnouncementChannelModal(true),
            },
          ]
        : []),
      {
        key: "2",
        label: t("notificationsDrawer.markAllAsRead"),
        icon: <CheckOutlined />,
        disabled: !unreadMessages || (unreadMessages.total === 0 && unreadMessages.unreadMessages?.length === 0),
        onClick: () => {
          void dispatch(chatActions.markAllAsRead());
        },
      },
    ],
    [t, unreadMessages, dispatch, sortByResponseTime, canWriteChat, canManageAnnouncementChannel],
  );

  return (
    <Drawer
      title={t("chatDrawer.title")}
      extra={
        <Dropdown menu={{ items: dropdown }}>
          <Button type="text">
            <Space>
              <MoreOutlined className="rotate-90 text-xl" />
            </Space>
          </Button>
        </Dropdown>
      }
      placement="right"
      onClose={() => toggleDrawer()}
      open={isOpen}
      mask={false}
      maskClosable={false}
      width={CHAT_DRAWER_WIDTH}
      bodyStyle={{ padding: 0, paddingTop: 0, height: "auto" }}
      className="drawer max-w-full"
    >
      <Tabs
        tabBarStyle={{ padding: "0 16px", backgroundColor: "white" }}
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as ChatDrawerTabKey)}
        items={items}
      />
      {canWriteChat ? (
        <SendMessageToMultipleUsersModal
          onClose={() => setShowMultipleUsersModal(false)}
          open={showMultipleUsersModal}
        />
      ) : null}
      {canManageAnnouncementChannel ? (
        <AnnouncementChannelModal
          onClose={() => setShowAnnouncementChannelModal(false)}
          open={showAnnouncementChannelModal}
          canWrite={canWriteAnnouncementChannel}
        />
      ) : null}
    </Drawer>
  );
};

export default ChatDrawer;
